import React, { useState, useEffect, useRef, useCallback } from 'react'
import { NavLink } from 'react-router-dom'
import {
  Newspaper, Share2, Video, Heart, ChevronLeft, ChevronRight,
  Play, X, Award, Users, ExternalLink, Star, Trophy, Camera
} from 'lucide-react'
import './Newsroom.css'

// ─────────────────────────────────────────────
// DATA — edit these arrays to add/update content
// ─────────────────────────────────────────────

// FEATURED (latest) — top slideshow, newest first
const featured = [
  {
    type: 'image',
    src: '/media/newsroom/featured1.jpg',
    tag: 'Print Media',
    title: 'The Hindu covers our hostel expansion drive',
    desc: 'A full-page feature in The Hindus Odisha edition highlighting our new 80-bed hostel block and the children it will serve.',
    date: 'May 2025',
  },
  {
    type: 'video',
    poster: '/media/newsroom/featured2-poster.jpg',
    videoUrl: 'https://youtu.be/jR19-eEgDjg?si=nqLq0PPR5dplHkYO',
    tag: 'Video Report',
    title: 'Sangharsa Tv segment on skill development centre',
    desc: 'Sangharsa Odisha visited our MSS Teachears Training Program.',
    date: 'April 2025',
  },
  {
    type: 'image',
    src: '/media/newsroom/featured3.jpg',
    tag: 'Social Media',
    title: 'Our Annual Day went viral — 2.4 lakh reach',
    desc: 'A reel from our Annual Day celebration reached 2.4 lakh people on Instagram and Facebook, bringing in 47 new donors.',
    date: 'March 2025',
  },
  {
    type: 'image',
    src: '/media/newsroom/featured4.jpg',
    tag: 'Print Media',
    title: 'Sambad daily — midday meal pilot launch',
    desc: 'Odia daily Sambad covered the launch of our midday meal pilot for 300 day-scholar children across 3 village schools.',
    date: 'February 2025',
  },
]

// ARCHIVE BLOCKS — older coverage, newest block first
// Each block: { year, label, items: [] }
const archiveBlocks = [
  {
    year: '2024',
    label: 'Coverage — 2024',
    items: [
      { type: 'image', src: '/media/newsroom/2024-01.jpg', tag: 'Print', title: 'Dharitri — 500 children milestone', desc: 'Dharitri newspaper documented our 500th enrolled child milestone with a photo essay from the campus.', date: 'Nov 2024' },
      { type: 'image', src: '/media/newsroom/2024-02.jpg', tag: 'Social', title: 'Facebook post — new computer lab', desc: 'Our post announcing 20 new computers donated by supporters reached 80,000 people organically.', date: 'Sep 2024' },
      { type: 'video', poster: '/media/newsroom/2024-03-poster.jpg', videoUrl: 'https://www.youtube.com/embed/YOUR_VIDEO_ID_2', tag: 'Video', title: 'YouTube: Independence Day celebration', desc: 'Our Independence Day flag hoisting and cultural program livestreamed for families of resident children.', date: 'Aug 2024' },
      { type: 'image', src: '/media/newsroom/2024-04.jpg', tag: 'Print', title: 'Prameya — vaccination camp coverage', desc: 'Prameya daily covered our district health department collaboration vaccination drive for 200 children.', date: 'Jun 2024' },
      { type: 'image', src: '/media/newsroom/2024-05.jpg', tag: 'Social', title: 'Instagram — skill training graduation', desc: 'Graduation ceremony reel for our 3rd skill training batch — 22 students, 18 immediately placed.', date: 'Apr 2024' },
    ],
  },
  {
    year: '2023',
    label: 'Coverage — 2023',
    items: [
      { type: 'image', src: '/media/newsroom/2023-01.jpg', tag: 'Print', title: 'Samaja — founder interview', desc: 'A long-form interview with our founder on the origin story and future vision of the Samiti.', date: 'Dec 2023' },
      { type: 'image', src: '/media/newsroom/2023-02.jpg', tag: 'Social', title: 'Childrens Day — viral photo essay', desc: 'A thread of 12 photos from our childrens day celebration shared 1,400 times on Facebook.', date: 'Nov 2023' },
      { type: 'video', poster: '/media/newsroom/2023-03-poster.jpg', videoUrl: 'https://www.youtube.com/embed/YOUR_VIDEO_ID_3', tag: 'Video', title: 'NDTV India: tribal education report', desc: 'NDTV Indias "India Matters" segment featured Malyabanta as a model for tribal child welfare.', date: 'Aug 2023' },
      { type: 'image', src: '/media/newsroom/2023-04.jpg', tag: 'Print', title: 'New Indian Express — hostel inauguration', desc: 'NIE covered the inauguration of our new hostel block with the district collector in attendance.', date: 'May 2023' },
    ],
  },
]

// DONORS — highest donation first
const donors = [
  {
    id: 'donor-1',
    name: 'Rajendra Kumar Patel',
    designation: 'Managing Director, Patel Steel Pvt. Ltd.',
    location: 'Bhubaneswar, Odisha',
    donated: 500000,
    cause: 'Hostel Block Construction',
    quote: '"Every child deserves a roof and a book. I am proud to have contributed to both at Malyabanta."',
    year: '2024',
    slides: [
      { type: 'cert' }, // certificate slide — auto-generated
      { type: 'image', src: '/media/donors/patel-1.jpg', caption: 'Presenting the cheque at the hostel inauguration ceremony' },
      { type: 'image', src: '/media/donors/patel-2.jpg', caption: 'Interaction with resident children at the new hostel block' },
      { type: 'image', src: '/media/donors/patel-3.jpg', caption: 'Felicitated by our founder and board members' },
      { type: 'video', poster: '/media/donors/patel-video-poster.jpg', videoUrl: 'https://www.youtube.com/embed/YOUR_DONOR_VIDEO', caption: 'Message from the donor — why he supports MSS' },
    ],
  },
  {
    id: 'donor-2',
    name: 'Dr. Meena Sharma',
    designation: 'Senior Consultant, AIIMS Bhubaneswar',
    location: 'Bhubaneswar, Odisha',
    donated: 250000,
    cause: 'Child Health & Nutrition Program',
    quote: '"Healthcare and education must go hand in hand. These children deserve both."',
    year: '2024',
    slides: [
      { type: 'cert' },
      { type: 'image', src: '/media/donors/sharma-1.jpg', caption: 'Conducting a health camp for resident children' },
      { type: 'image', src: '/media/donors/sharma-2.jpg', caption: 'With our child welfare team at the health check-up drive' },
      { type: 'image', src: '/media/donors/sharma-3.jpg', caption: 'Distributing hygiene kits to 120 hostel children' },
    ],
  },
  {
    id: 'donor-3',
    name: 'Anand & Priya Nayak Family',
    designation: 'NRI Philanthropists',
    location: 'New Jersey, USA',
    donated: 180000,
    cause: 'Computer Lab Expansion',
    quote: '"We grew up in Odisha. Giving back through education is the most meaningful thing we can do."',
    year: '2023',
    slides: [
      { type: 'cert' },
      { type: 'image', src: '/media/donors/nayak-1.jpg', caption: 'Virtual handover ceremony for the 20 donated computers' },
      { type: 'image', src: '/media/donors/nayak-2.jpg', caption: 'Children using the new computer lab — first session' },
    ],
  },
  {
    id: 'donor-4',
    name: 'Sunita Devi Foundation',
    designation: 'Charitable Trust, Cuttack',
    location: 'Cuttack, Odisha',
    donated: 120000,
    cause: 'Mid-Day Meal Program',
    quote: '"No child should study on an empty stomach. This program ensures they do not."',
    year: '2023',
    slides: [
      { type: 'cert' },
      { type: 'image', src: '/media/donors/foundation-1.jpg', caption: 'Inauguration of the community kitchen with foundation trustees' },
      { type: 'image', src: '/media/donors/foundation-2.jpg', caption: 'First meal served under the pilot program' },
    ],
  },
]

// ─────────────────────────────────────────────
// UTILITIES
// ─────────────────────────────────────────────

const TAG_COLORS = {
  'Print Media': { bg: '#0c4a6e', text: '#bae6fd' },
  'Print':       { bg: '#0c4a6e', text: '#bae6fd' },
  'Video Report':{ bg: '#7c3aed', text: '#ddd6fe' },
  'Video':       { bg: '#7c3aed', text: '#ddd6fe' },
  'Social Media':{ bg: '#0f766e', text: '#99f6e4' },
  'Social':      { bg: '#0f766e', text: '#99f6e4' },
}

function Tag({ label }) {
  const c = TAG_COLORS[label] || { bg: '#1e293b', text: '#e2e8f0' }
  return (
    <span className="nr-tag" style={{ background: c.bg, color: c.text }}>
      {label === 'Print Media' || label === 'Print' ? <Newspaper size={11}/> :
       label === 'Video Report' || label === 'Video' ? <Video size={11}/> :
       <Share2 size={11}/>}
      {label}
    </span>
  )
}

function makePlaceholder(color, label) {
  return `data:image/svg+xml,${encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="800" height="500">
      <rect width="800" height="500" fill="${color}"/>
      <text x="400" y="240" text-anchor="middle" font-size="20" fill="rgba(255,255,255,0.8)" font-family="sans-serif" font-weight="bold">${label}</text>
      <text x="400" y="272" text-anchor="middle" font-size="13" fill="rgba(255,255,255,0.5)" font-family="sans-serif">Replace with actual photo</text>
    </svg>`
  )}`
}

function getMediaSrc(item) {
  // fallback to placeholder if file not found (dev mode)
  return item.src || makePlaceholder('#0c4a6e', item.title || 'Photo')
}
function getPosterSrc(item) {
  return item.poster || makePlaceholder('#4c1d95', item.title || 'Video')
}

// ─────────────────────────────────────────────
// FEATURED SLIDESHOW (hero, auto-plays every 6s)
// ─────────────────────────────────────────────

function FeaturedSlideshow() {
  const [cur, setCur] = useState(0)
  const [playing, setPlaying] = useState(false)
  const timerRef = useRef(null)

  const go = useCallback((dir) => {
    setCur(c => (c + dir + featured.length) % featured.length)
    setPlaying(false)
    clearInterval(timerRef.current)
    timerRef.current = setInterval(() => setCur(c => (c + 1) % featured.length), 6000)
  }, [])

  useEffect(() => {
    timerRef.current = setInterval(() => setCur(c => (c + 1) % featured.length), 6000)
    return () => clearInterval(timerRef.current)
  }, [])

  const item = featured[cur]

  return (
    <div className="nr-featured">
      <div className="nr-featured__viewport">
        {/* slides */}
        {featured.map((slide, i) => (
          <div key={i} className={`nr-featured__slide${i === cur ? ' active' : ''}`}>
            {slide.type === 'video' ? (
              <div className="nr-featured__video-wrap">
                {playing && cur === i ? (
                  <iframe
                    src={slide.videoUrl + '?autoplay=1'}
                    title={slide.title}
                    allowFullScreen
                    allow="autoplay"
                    className="nr-featured__iframe"
                  />
                ) : (
                  <img src={getPosterSrc(slide)} alt={slide.title} className="nr-featured__img"/>
                )}
              </div>
            ) : (
              <img src={getMediaSrc(slide)} alt={slide.title} className="nr-featured__img"/>
            )}
          </div>
        ))}

        {/* overlay info */}
        <div className="nr-featured__overlay">
          <div className="nr-featured__meta">
            <Tag label={item.tag}/>
            <span className="nr-featured__date">{item.date}</span>
          </div>
          <h2 className="nr-featured__title">{item.title}</h2>
          <p className="nr-featured__desc">{item.desc}</p>
          {item.type === 'video' && !playing && (
            <button className="nr-featured__play" onClick={() => setPlaying(true)}>
              <Play size={18} fill="white"/> Watch Now
            </button>
          )}
        </div>

        {/* nav */}
        <button className="nr-featured__nav nr-featured__nav--prev" onClick={() => go(-1)}>
          <ChevronLeft size={22}/>
        </button>
        <button className="nr-featured__nav nr-featured__nav--next" onClick={() => go(1)}>
          <ChevronRight size={22}/>
        </button>

        {/* progress bar */}
        <div className="nr-featured__progress" key={cur}>
          <div className="nr-featured__progress-fill"/>
        </div>

        {/* dots */}
        <div className="nr-featured__dots">
          {featured.map((_, i) => (
            <button
              key={i}
              className={`nr-featured__dot${i === cur ? ' active' : ''}`}
              onClick={() => { setCur(i); setPlaying(false); clearInterval(timerRef.current); timerRef.current = setInterval(() => setCur(c => (c + 1) % featured.length), 6000) }}
            />
          ))}
        </div>
      </div>

      {/* thumbnail strip */}
      <div className="nr-featured__thumbs">
        {featured.map((slide, i) => (
          <button
            key={i}
            className={`nr-featured__thumb${i === cur ? ' active' : ''}`}
            onClick={() => { setCur(i); setPlaying(false) }}
          >
            <img src={slide.type === 'video' ? getPosterSrc(slide) : getMediaSrc(slide)} alt={slide.title}/>
            <div className="nr-featured__thumb-label">
              <Tag label={slide.tag}/>
              <span>{slide.title}</span>
            </div>
          </button>
        ))}
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────
// ARCHIVE BLOCK SLIDESHOW
// ─────────────────────────────────────────────

function ArchiveSlideshow({ items }) {
  const [cur, setCur] = useState(0)
  const [playing, setPlaying] = useState(false)
  const go = (dir) => { setCur(c => (c + dir + items.length) % items.length); setPlaying(false) }
  const item = items[cur]

  return (
    <div className="nr-archive-slide">
      <div className="nr-archive-slide__viewport">
        {item.type === 'video' ? (
          playing ? (
              // <iframe src={item.videoUrl + '?autoplay=1'} title={item.title} allowFullScreen allow="autoplay" className="nr-archive-slide__iframe"/>
      

           <iframe
            src={item.videoUrl.replace('watch?v=','embed/').replace('youtu.be/','www.youtube.com/embed/') + '?autoplay=1'}
            title={item.title} allowFullScreen
            allow="autoplay; encrypted-media; picture-in-picture"
            className="nr-archive-slide__iframe"
          />

          ) : (
            <div className="nr-archive-slide__video-wrap">
              <img src={getPosterSrc(item)} alt={item.title} className="nr-archive-slide__img"/>
              <button className="nr-archive-slide__play" onClick={() => setPlaying(true)}>
                <Play size={20} fill="white"/>
              </button>
            </div>
          )
        ) : (
          <img src={getMediaSrc(item)} alt={item.title} className="nr-archive-slide__img"/>
        )}

        {items.length > 1 && (
          <>
            <button className="nr-slide-nav nr-slide-nav--prev" onClick={() => go(-1)}><ChevronLeft size={16}/></button>
            <button className="nr-slide-nav nr-slide-nav--next" onClick={() => go(1)}><ChevronRight size={16}/></button>
          </>
        )}

        <div className="nr-archive-slide__counter">{cur + 1} / {items.length}</div>
      </div>

      <div className="nr-archive-slide__info">
        <div className="nr-archive-slide__meta">
          <Tag label={item.tag}/>
          <span className="nr-archive-slide__date">{item.date}</span>
        </div>
        <h4 className="nr-archive-slide__title">{item.title}</h4>
        <p className="nr-archive-slide__desc">{item.desc}</p>
      </div>

      {items.length > 1 && (
        <div className="nr-archive-slide__dots">
          {items.map((_, i) => (
            <button key={i} className={`nr-dot${i === cur ? ' active' : ''}`} onClick={() => { setCur(i); setPlaying(false) }}/>
          ))}
        </div>
      )}
    </div>
  )
}

// ─────────────────────────────────────────────
// DONOR CERTIFICATE SLIDE
// ─────────────────────────────────────────────

function DonorCertSlide({ donor }) {
  return (
    <div className="nr-cert">
      <div className="nr-cert__border"/>
      <div className="nr-cert__corner nr-cert__corner--tl"/>
      <div className="nr-cert__corner nr-cert__corner--tr"/>
      <div className="nr-cert__corner nr-cert__corner--bl"/>
      <div className="nr-cert__corner nr-cert__corner--br"/>

      <div className="nr-cert__logo">MSS</div>
      <div className="nr-cert__org">Malyabanta Swechhasevi Samiti</div>
      <div className="nr-cert__divider"/>

      <p className="nr-cert__label">Certificate of Gratitude</p>
      <p className="nr-cert__presented">This certificate is gratefully presented to</p>

      <h2 className="nr-cert__name">{donor.name}</h2>
      <p className="nr-cert__desig">{donor.designation}</p>
      <p className="nr-cert__location">{donor.location}</p>

      <div className="nr-cert__amount-wrap">
        <Trophy size={16} className="nr-cert__trophy"/>
        <span className="nr-cert__amount">₹{donor.donated.toLocaleString()}</span>
        <span className="nr-cert__amount-label"> donated towards <em>{donor.cause}</em></span>
      </div>

      <blockquote className="nr-cert__quote">{donor.quote}</blockquote>

      <div className="nr-cert__footer">
        <div className="nr-cert__sign">
          <div className="nr-cert__sign-line"/>
          <span>President, MSS</span>
        </div>
        <div className="nr-cert__year">{donor.year}</div>
        <div className="nr-cert__sign">
          <div className="nr-cert__sign-line"/>
          <span>Secretary, MSS</span>
        </div>
      </div>

      <div className="nr-cert__watermark">MSS</div>
    </div>
  )
}

// ─────────────────────────────────────────────
// DONOR MODAL SLIDESHOW
// ─────────────────────────────────────────────

function DonorModal({ donor, onClose }) {
  const [cur, setCur] = useState(0)
  const [playing, setPlaying] = useState(false)
  const go = (dir) => { setCur(c => (c + dir + donor.slides.length) % donor.slides.length); setPlaying(false) }
  const slide = donor.slides[cur]

  // close on Escape
  useEffect(() => {
    const fn = (e) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', fn)
    return () => window.removeEventListener('keydown', fn)
  }, [onClose])

  return (
    <div className="nr-modal-overlay" onClick={onClose}>
      <div className="nr-modal" onClick={e => e.stopPropagation()}>
        <button className="nr-modal__close" onClick={onClose}><X size={20}/></button>

        <div className="nr-modal__viewport">
          {slide.type === 'cert' ? (
            <DonorCertSlide donor={donor}/>
          ) : slide.type === 'video' ? (
            playing ? (
              <iframe src={slide.videoUrl + '?autoplay=1'} title={donor.name} allowFullScreen allow="autoplay" className="nr-modal__iframe"/>
            ) : (
              <div className="nr-modal__video-wrap">
                <img src={getPosterSrc(slide)} alt={slide.caption} className="nr-modal__img"/>
                <button className="nr-modal__play" onClick={() => setPlaying(true)}>
                  <Play size={28} fill="white"/>
                </button>
              </div>
            )
          ) : (
            <img src={getMediaSrc(slide)} alt={slide.caption} className="nr-modal__img"/>
          )}

          {donor.slides.length > 1 && (
            <>
              <button className="nr-modal__nav nr-modal__nav--prev" onClick={() => go(-1)}><ChevronLeft size={22}/></button>
              <button className="nr-modal__nav nr-modal__nav--next" onClick={() => go(1)}><ChevronRight size={22}/></button>
            </>
          )}
        </div>

        {slide.type !== 'cert' && slide.caption && (
          <div className="nr-modal__caption">{slide.caption}</div>
        )}

        <div className="nr-modal__dots">
          {donor.slides.map((s, i) => (
            <button
              key={i}
              className={`nr-modal__dot${i === cur ? ' active' : ''}`}
              onClick={() => { setCur(i); setPlaying(false) }}
              title={s.type === 'cert' ? 'Certificate' : s.caption}
            >
              {s.type === 'cert' ? <Award size={12}/> : s.type === 'video' ? <Play size={12}/> : <Camera size={12}/>}
            </button>
          ))}
        </div>

        <div className="nr-modal__info">
          <div className="nr-modal__donor-name">{donor.name}</div>
          <div className="nr-modal__donor-desig">{donor.designation} · {donor.location}</div>
        </div>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────
// DONOR CARD (in the donors grid)
// ─────────────────────────────────────────────

function DonorCard({ donor, rank, onClick }) {
  return (
    <button className={`nr-donor-card${rank === 1 ? ' nr-donor-card--top' : ''}`} onClick={onClick}>
      {rank === 1 && <div className="nr-donor-card__crown"><Trophy size={14}/> Top Donor</div>}

      <div className="nr-donor-card__avatar">
        {donor.name.charAt(0)}
        <div className="nr-donor-card__avatar-ring"/>
      </div>

      <div className="nr-donor-card__body">
        <div className="nr-donor-card__name">{donor.name}</div>
        <div className="nr-donor-card__desig">{donor.designation}</div>
        <div className="nr-donor-card__location">{donor.location}</div>
        <div className="nr-donor-card__cause">{donor.cause}</div>
      </div>

      <div className="nr-donor-card__amount">
        <Heart size={13} fill="currentColor"/>
        ₹{donor.donated.toLocaleString()}
      </div>

      <div className="nr-donor-card__cta">View Story <ExternalLink size={13}/></div>
    </button>
  )
}

// ─────────────────────────────────────────────
// REVEAL WRAPPER
// ─────────────────────────────────────────────

function Reveal({ children, className }) {
  const ref = useRef(null)
  useEffect(() => {
    const o = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { ref.current.classList.add('nr-visible'); o.disconnect() }
    }, { threshold: 0.08 })
    if (ref.current) o.observe(ref.current)
    return () => o.disconnect()
  }, [])
  return <div ref={ref} className={`nr-reveal ${className || ''}`}>{children}</div>
}

// ─────────────────────────────────────────────
// PINNED VIDEO — configure here
// ─────────────────────────────────────────────

const pinnedLeft = {
  type: 'html',
  src: '/fb-argus-news-post.html',
  title: 'Argus News — Latest Coverage',
  desc: 'Our most recent Facebook video coverage by Argus News.',
  tag: 'Video Report',
  date: 'May 2025',
}

const pinnedRight = {
  type: 'youtube',
  src: 'https://www.youtube.com/watch?v=jR19-eEgDjg', // ← paste your link here
  title: 'YouTube Sangharsa Tv — Featured Report',
  desc: 'Sangharsa Odisha visited our MSS Teachears Training Program.',
  tag: 'Video Report',
  date: 'May 2025',
}

// ─────────────────────────────────────────────
// PINNED VIDEO COMPONENT
// ─────────────────────────────────────────────

function PinnedVideoCard({ video }) {
  const embedSrc = video.type === 'youtube'
    ? video.src.replace('watch?v=', 'embed/').replace('youtu.be/', 'www.youtube.com/embed/')
    : video.src
  return (
    <div className="nr-pinned-card">
      <div className="nr-pinned-card__frame-wrap">
        <iframe src={embedSrc} title={video.title} className="nr-pinned-card__frame"
          style={{ border: 'none' }} allowFullScreen
          allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"/>
      </div>
      <div className="nr-pinned-card__info">
        <div className="nr-pinned-card__meta">
          <Tag label={video.tag}/>
          <span className="nr-pinned-card__date">{video.date}</span>
        </div>
        <h3 className="nr-pinned-card__title">{video.title}</h3>
        <p className="nr-pinned-card__desc">{video.desc}</p>
      </div>
    </div>
  )
}

function PinnedVideos() {
  return (
    <div className="nr-pinned">
      <div className="nr-pinned__badge"><Video size={13}/> Featured Videos</div>
      <div className="nr-pinned__two">
        <PinnedVideoCard video={pinnedLeft}/>
        <PinnedVideoCard video={pinnedRight}/>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────
// MAIN PAGE
// ─────────────────────────────────────────────

const TABS = [
  { id: 'coverage', label: 'Coverage', icon: Newspaper },
  { id: 'donors',   label: 'Donor Spotlights', icon: Trophy },
]

export default function Newsroom() {
  const [activeTab, setActiveTab] = useState('coverage')
  const [activeDonor, setActiveDonor] = useState(null)

  const sortedDonors = [...donors].sort((a, b) => b.donated - a.donated)

  useEffect(() => {
    document.body.style.overflow = activeDonor ? 'hidden' : ''
    return () => { document.body.style.overflow = '' }
  }, [activeDonor])

  return (
    <div className="newsroom-page">

      {/* ── HERO ── */}
      <section className="nr-hero">
        <div className="nr-hero__bg">
          <div className="nr-hero__orb nr-hero__orb--1"/>
          <div className="nr-hero__orb nr-hero__orb--2"/>
        </div>
        <div className="container nr-hero__content">
          <span className="section-tag">Press · Social · Video</span>
          <h1 className="nr-hero__title">Newsroom</h1>
          <p className="nr-hero__desc">
            Every story told about our work is a story about a child whose future changed.
            Browse our press coverage, social media moments, video reports — and meet the people who made it possible.
          </p>

          {/* ── TABS ── */}
          <div className="nr-tabs">
            {TABS.map(tab => (
              <button
                key={tab.id}
                className={`nr-tab${activeTab === tab.id ? ' nr-tab--active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <tab.icon size={16}/>
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════ */}
      {/* TAB 1 — COVERAGE                      */}
      {/* ══════════════════════════════════════ */}
      {activeTab === 'coverage' && (
        <>
          {/* ── PINNED VIDEO ── */}
          <section className="page-section nr-section" style={{paddingBottom:0}}>
            <div className="container">
              <Reveal>
                <div className="nr-section-head">
                  <div>
                    <span className="section-tag">Now Playing</span>
                    <h2 className="section-title" style={{marginBottom:0}}>Featured Video</h2>
                  </div>
                  <p className="nr-section-sub">Pinned at top — our most recent video coverage, playing by default.</p>
                </div>
              </Reveal>
              <Reveal>
                <PinnedVideos/>
              </Reveal>
            </div>
          </section>

          {/* ── LATEST SLIDESHOW ── */}
          <section id="latest" className="page-section nr-section">
            <div className="container">
              <Reveal>
                <div className="nr-section-head">
                  <div>
                    <span className="section-tag">Latest</span>
                    <h2 className="section-title" style={{marginBottom:0}}>Recent Coverage</h2>
                  </div>
                  <p className="nr-section-sub">Newest stories on top — print clippings, social posts, and video reports.</p>
                </div>
              </Reveal>
              <Reveal>
                <FeaturedSlideshow/>
              </Reveal>
            </div>
          </section>

          {/* ── ARCHIVE BLOCKS ── */}
          <section id="archive" className="nr-archive-section">
            <div className="container">
              <Reveal>
                <div className="nr-section-head">
                  <div>
                    <span className="section-tag">Archive</span>
                    <h2 className="section-title" style={{marginBottom:0}}>Past Coverage</h2>
                  </div>
                  <p className="nr-section-sub">Earlier media features, social posts and video reports organised by year.</p>
                </div>
              </Reveal>
              {archiveBlocks.map((block) => (
                <Reveal key={block.year}>
                  <div className="nr-archive-block">
                    <div className="nr-archive-block__header">
                      <span className="nr-archive-block__year">{block.year}</span>
                      <span className="nr-archive-block__label">{block.label}</span>
                      <div className="nr-archive-block__line"/>
                    </div>
                    <div className="nr-archive-grid">
                      {block.items.map((item, i) => (
                        <ArchiveSlideshow key={i} items={[item]}/>
                      ))}
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </section>
        </>
      )}

      {/* ══════════════════════════════════════ */}
      {/* TAB 2 — DONOR SPOTLIGHTS              */}
      {/* ══════════════════════════════════════ */}
      {activeTab === 'donors' && (
        <section className="page-section nr-donors-section">
          <div className="container">
            <Reveal>
              <div className="nr-section-head">
                <div>
                  <span className="section-tag">Donor Spotlights</span>
                  <h2 className="section-title" style={{marginBottom:0}}>Faces Behind the Change</h2>
                </div>
                <p className="nr-section-sub">
                  Each donor's story is a slideshow — a certificate, photographs, and a video message.
                  Click any card to view their full story.
                </p>
              </div>
            </Reveal>

            <Reveal>
              <div className="nr-donor-grid">
                {sortedDonors.map((donor, i) => (
                  <DonorCard
                    key={donor.id}
                    donor={donor}
                    rank={i + 1}
                    onClick={() => setActiveDonor(donor)}
                  />
                ))}
              </div>
            </Reveal>

            <Reveal>
              <div className="nr-donor-cta">
                <Heart size={18} fill="currentColor"/>
                Want to see your name here?
                <NavLink to="/donate" className="btn btn-primary" style={{marginLeft:12}}>Donate Now</NavLink>
              </div>
            </Reveal>
          </div>
        </section>
      )}

      {/* ── DONOR MODAL ── */}
      {activeDonor && (
        <DonorModal donor={activeDonor} onClose={() => setActiveDonor(null)}/>
      )}
    </div>
  )
}
